import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileUp, Loader2, ExternalLink, Send, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 25;

/**
 * UploadPdfProposalCard — broker uploads a PDF generated on another platform
 * (lender software, MyCRM, ASIC export, etc.) and the system turns it into a
 * live proposal deck. Each PDF page becomes one slide rendered in the
 * existing WebRTC meeting room.
 *
 * Flow on submit:
 *   1. POST /api/breakdown/upload-pdf-proposal (multipart: file + client_name)
 *   2. Receive {proposal_id, meeting_id, room_id, guest_join_url, host_join_url, pages}
 *   3. Show a "ready to share" card with the guest link to copy/open the
 *      host link to enter the meeting and start presenting.
 */
const UploadPdfProposalCard = () => {
  const { token } = useAuth();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const pickFile = (f) => {
    if (!f) return;
    if (f.type && f.type !== 'application/pdf') {
      toast.error('Only PDF files are accepted.');
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (${(f.size / 1_048_576).toFixed(1)} MB). Max ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) pickFile(f);
  };

  const submit = async () => {
    if (!file) {
      toast.error('Choose a PDF first.');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (clientName.trim()) fd.append('client_name', clientName.trim());
    if (title.trim()) fd.append('title', title.trim());
    try {
      const res = await fetch(`${BACKEND}/api/breakdown/upload-pdf-proposal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch (e) { /* */ }
        throw new Error(detail);
      }
      const data = await res.json();
      setResult(data);
      toast.success(`PDF converted to ${data.pages}-page live proposal — ready to share.`);
    } catch (e) {
      toast.error(`Upload failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const guestFullUrl = result ? `${window.location.origin}${result.guest_join_url}` : null;
  const hostFullUrl = result ? `${window.location.origin}${result.host_join_url}` : null;

  const copyGuest = async () => {
    if (!guestFullUrl) return;
    try {
      await navigator.clipboard.writeText(guestFullUrl);
      toast.success('Guest link copied to clipboard.');
    } catch (e) {
      toast.error('Could not copy. Highlight and copy manually.');
    }
  };

  const reset = () => {
    setFile(null);
    setClientName('');
    setTitle('');
    setResult(null);
  };

  return (
    <Card className="border-2 border-sky-200 shadow-lg" data-testid="upload-pdf-card">
      <CardHeader className="bg-gradient-to-r from-sky-50 via-cyan-50 to-blue-50">
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <FileUp className="h-5 w-5 text-sky-600" />
          Upload a PDF Proposal to Present Live
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Have a proposal PDF from another platform (lender software, MyCRM, ASIC export)? Upload it here and we'll turn it into a live proposal you can present page-by-page over video. Max {MAX_MB} MB.
        </p>
      </CardHeader>

      <CardContent className="pt-6 space-y-4">
        {!result && (
          <>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-6 cursor-pointer text-center border-slate-300 hover:border-sky-400 hover:bg-sky-50 transition"
              data-testid="upload-pdf-dropzone"
              tabIndex={0}
            >
              <FileUp className="h-8 w-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-medium text-slate-700">
                {file ? file.name : (<>Drag a PDF here or <span className="text-sky-600 underline">browse</span></>)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {file ? `${(file.size / 1_048_576).toFixed(1)} MB` : `PDF only · max ${MAX_MB} MB`}
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="upload-pdf-file-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Client name</label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Kris & Emma"
                  data-testid="upload-pdf-client-name"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Proposal title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Refinance Strategy"
                  data-testid="upload-pdf-title"
                />
              </div>
            </div>

            <Button
              onClick={submit}
              disabled={submitting || !file}
              className="w-full bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white"
              data-testid="upload-pdf-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Converting PDF…</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Create Live Proposal from PDF</>
              )}
            </Button>
          </>
        )}

        {result && (
          <div className="space-y-4" data-testid="upload-pdf-result">
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-5">
              <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Live proposal ready</p>
              <p className="text-lg font-bold text-slate-900 mt-1">
                {result.pages}-page deck created for {clientName || 'your client'}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Share the guest link below with your client. They join in their browser — no app, no login.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Guest join link (share with client)</label>
              <div className="flex gap-2">
                <Input value={guestFullUrl} readOnly className="font-mono text-sm" data-testid="upload-pdf-guest-link" onFocus={(e) => e.target.select()} />
                <Button onClick={copyGuest} variant="outline" className="border-slate-300" data-testid="upload-pdf-copy-btn">
                  <Copy className="h-4 w-4 mr-1" /> Copy
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={() => window.open(hostFullUrl, '_blank', 'noopener')}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="upload-pdf-host-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Open Meeting as Host
              </Button>
              <Button onClick={reset} variant="outline" className="border-slate-300" data-testid="upload-pdf-reset-btn">
                Upload another PDF
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UploadPdfProposalCard;
